'use client'

import { reviewModalContent } from '@/app/[locale]/account/_components/constants'
import { ProductInfoRating } from '@/app/[locale]/product/[slug]/_components/product-info/product-info-rating'
import { ExpandableText } from '@/components/ui/expandable-text'
import { Tag } from '@/components/ui/tag'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { DateUtil } from '@/lib/utils/date'
import { ReviewItem } from '@/types/collection'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import Image from 'next/image'

const ProductReviewItemGallery = dynamic(
  () =>
    import(
      '@/app/[locale]/product/[slug]/_components/product-reviews/product-review-item-gallery'
    ).then((mod) => mod.ProductReviewItemGallery),
  {
    loading: () => null,
  }
)

type Props = {
  review: ReviewItem
}
type GalleryImage = {
  largeURL: string
  thumbnailURL: string
  width: number
  height: number
}

// Đường dẫn ảnh logo các nguồn
const getReviewTagLabel = (key: string) => {
  const allTags = [
    ...reviewModalContent.positiveTags,
    ...reviewModalContent.tags.flat(),
  ]
  const tag = allTags.find((t) => t.key === key)

  return tag?.label ?? key
}

// Đường dẫn ảnh logo các nguồn
const IMAGE_SOURCE = {
  shopee: '/icons/third-party/shopee.svg',
  lazada: '/icons/third-party/lazada.svg',
  tiktok: '/icons/third-party/tiktok.svg',
}

export const ProductReviewsItem = ({ review }: Props) => {
  const t = useTranslations('product_detail.title')
  const tReview = useTranslations()

  let reviewImages: GalleryImage[] = []
  if (review?.images?.length) {
    reviewImages = review.images.map((image) => {
      const isValidUrl =
        typeof image === 'string' &&
        (image.startsWith('http://') || image.startsWith('https://'))
      const url = isValidUrl ? image : ImageHelper.getImageUrl(image)
      return {
        largeURL: url,
        thumbnailURL: url,
        width: 1200,
        height: 1600,
      }
    })
  }

  return (
    <article aria-label='Product review item' role='listitem'>
      <div className='relative rounded-xl bg-light p-6'>
        <div className='flex items-center'>
          <p className='line-clamp-1 font-sans text-base font-medium leading-5 text-neutral-800 max-md:w-2/3'>
            {review.fullname}
          </p>
          <span className='mx-3 inline-block h-1 w-1 rounded-full bg-neutral-300'></span>
          <time className='font-sans text-sm leading-4.5 text-neutral-400'>
            {DateUtil.formatShort(review.created_at)}
          </time>
        </div>
        <ProductInfoRating
          rating={{ avg: review.score, count: 0 }}
          showText={false}
          showAvgText={false}
          className='my-2 [&_img]:h-4.5 [&_img]:w-4.5'
        />

        <div className='flex flex-col gap-y-1'>
          <ul className='flex flex-wrap items-center gap-x-8'>
            {[
              {
                title: tReview('account_page.review_body_fit.size'),
                value: review.size,
                isShow: !!review.size,
              },
              {
                title: tReview('account_page.review_body_fit.color'),
                value: review.color,
                isShow: !!review.color,
              },
            ].map((item) => {
              if (!item.isShow || !item.value) return null
              return (
                <li className='flex items-start gap-1' key={item.title}>
                  <p className='font-sans text-sm font-bold text-neutral-400'>
                    {item.title}:
                  </p>
                  <p className='font-sans text-sm text-neutral-400'>
                    {item.value}
                  </p>
                </li>
              )
            })}
          </ul>

          <ul className='grid grid-cols-2 gap-x-8 gap-y-1 lg:flex lg:flex-wrap lg:items-center lg:gap-x-8'>
            {[
              {
                title: tReview('account_page.review_body_fit.title'),
                value: tReview(
                  `account_page.review_body_fit.${review.body_fit}`
                ),
                isShow: !!review.body_fit,
                className: 'col-span-2 lg:col-span-auto',
              },
              {
                title: tReview('account_page.review_body_fit.height'),
                value: review.height ? `${review.height}cm` : '',
                isShow: !!review.height,
              },
              {
                title: tReview('account_page.review_body_fit.weight'),
                value: review.weight ? `${review.weight}kg` : '',
                isShow: !!review.weight,
              },
            ].map((item) => {
              if (!item.isShow || !item.value) return null
              return (
                <li
                  className={`flex items-start gap-1 ${item.className || ''}`}
                  key={item.title}
                >
                  <p className='font-sans text-sm font-bold text-neutral-400'>
                    {item.title}:
                  </p>
                  <p className='font-sans text-sm text-neutral-400'>
                    {item.value}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
        <ExpandableText
          className='mt-2 text-neutral-700 lg:mt-4 lg:text-base lg:leading-5.5'
          text={review.message}
          maxLines={4}
        />
        {reviewImages.length > 0 && (
          <ProductReviewItemGallery images={reviewImages} />
        )}

        {/* TODO: chưa có data */}
        <ul className='mt-4 flex items-start gap-1 overflow-x-auto pb-2 lg:hidden'>
          {review?.criteria?.map((item) => {
            const label = getReviewTagLabel(item)
            return (
              <li key={item}>
                <Tag title={tReview(label)} className='h-8 rounded-full' />
              </li>
            )
          })}
        </ul>

        <div className='mt-4 flex items-center justify-between lg:mt-6'>
          {/* TODO: chưa có data */}
          <ul className='flex items-start gap-1 max-lg:hidden'>
            {review?.criteria?.map((item) => {
              const label = getReviewTagLabel(item)
              return (
                <li key={item}>
                  <Tag title={tReview(label)} className='h-8 rounded-full' />
                </li>
              )
            })}
          </ul>
          {/* <div className='flex items-center gap-1'>
            <p className='font-sans text-xs text-neutral-400'>
              Đánh giá này có <br className='lg:hidden' /> hữư ích không?
            </p>
            <div className='group flex cursor-pointer gap-1'>
              <ThumbsUp
                className='text-neutral-500 group-hover:text-primary'
                width={22}
                height={22}
              />
              <p className='font-sans text-base text-neutral-400 group-hover:text-primary'>
                {' '}
                20
              </p>
            </div>
          </div> */}

          {review.source && review.source in IMAGE_SOURCE && (
            <div className='lg:absolute lg:right-6 lg:top-6 lg:flex lg:items-center lg:gap-2'>
              <p className='mb-1 font-sans text-xs italic text-neutral-900/50'>
                {t('review_from')}
              </p>
              <Image
                src={IMAGE_SOURCE[review.source as keyof typeof IMAGE_SOURCE]}
                width={60}
                height={18}
                alt='source icon'
                draggable={false}
              />
            </div>
          )}
        </div>
      </div>
      {review.feedback && <ReviewItemReply feedback={review.feedback} />}
    </article>
  )
}

const ReviewItemReply = ({ feedback }: { feedback: string }) => {
  const t = useTranslations('product_detail.title')

  return (
    <div className='relative mt-4 rounded-xl bg-neutral-200 px-6 py-4 md:ml-10'>
      <div className='absolute -top-3 left-10 h-0 w-0 rounded-sm border-x-[12px] border-b-[12px] border-x-transparent border-b-neutral-200' />

      <div className='mb-2 flex items-center'>
        <Image
          src={'/images/logo-circle.svg'}
          alt='logo coolmate '
          width={28}
          height={28}
          draggable={false}
        />
        <p className='ml-1 font-sans text-base font-medium leading-5.5 text-neutral-800'>
          {t('reply_from_coolmate')}
        </p>
        <span className='mx-1 inline-block h-1 w-1 rounded-full bg-neutral-300 lg:mx-3'></span>
      </div>
      <ExpandableText
        text={feedback}
        maxLines={3}
        expandText={t('read_more')}
        className='lg:text-base lg:leading-5.5'
      />
    </div>
  )
}
